import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff } from "lucide-react";
import {
  DataList,
  FormDialog,
  PageHeader,
  StatusPill,
  type DataListColumn,
} from "@ozpanel/kit";
import { Button, Eyebrow, Panel, Section, StateView, cn, useAuth, useToast } from "@ozpanel/ui";
import { useMutation as useMut } from "@tanstack/react-query";
import { usePushEnable } from "../usePushEnable";
import { AlertReading, type AlertWire } from "./alertState";

// NotificationsScreen — the alert policy had a complete backend (S5: /realtime/alerts/policy,
// the notify module's VAPID webpush, per-principal subscriptions) and NO user interface at
// all, so the thresholds that decide what reaches an operator were only settable by curl.
//
// Two independent thresholds, deliberately: a sound is an interruption you cannot dismiss,
// and a push follows you off the machine. They should not be forced to the same level.

// The vocabulary is the SERVER'S, verified against notify/policy.go's own validator
// ("priority must be one of: info, warn, error, crit") — the first version invented
// "critical" and "off" and every save was rejected. There is deliberately no "off": a
// channel is silenced by raising its threshold, not by a separate disabled state.
type Priority = "info" | "warn" | "error" | "crit";

const LEVELS: { key: Priority; label: string; detail: string }[] = [
  { key: "info", label: "Everything", detail: "Every event, including routine ones." },
  { key: "warn", label: "Warnings and worse", detail: "Something needs looking at eventually." },
  { key: "error", label: "Errors and worse", detail: "Something is failing now." },
  { key: "crit", label: "Critical only", detail: "Service is down or data is at risk." },
];

interface Policy {
  sound_min_priority: Priority;
  push_min_priority: Priority;
  // Out-of-band channels (notify/outofband.go): every alert that reaches push also goes to these, and they
  // keep working while the database is down (the policy's local copy carries them).
  webhook_urls?: string[];
  alert_emails?: string[];
  updated_at?: string;
  // "stored" (read now), "cached" (the database could not be read: the last policy read on this
  // node) or "default" (never read here: the schema's defaults). Shown when it is not "stored" —
  // a policy that could not be read must not look like the one that is set.
  source?: "stored" | "cached" | "default";
  note?: string;
}

interface AdminPushSubscription {
  id: number;
  user_agent: string;
  created_at: string;
  last_push_at?: string;
}

export function NotificationsScreen() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();

  const policy = useQuery({
    queryKey: ["alert-policy"],
    // Root-mounted, NOT under /api/v1 — a long-lived realtime route cannot live behind the
    // API group's request timeout.
    queryFn: () => client.rootFetch<Policy>("/realtime/alerts/policy"),
  });

  // PUT replaces the WHOLE policy — handlePutPolicy decodes the entire struct, so a partial
  // body leaves the other threshold empty and the validator rejects it. Merge, don't patch.
  const save = useMutation({
    mutationFn: (patch: Partial<Policy>) =>
      client.rootFetch<Policy>("/realtime/alerts/policy", {
        method: "PUT",
        body: JSON.stringify({
          sound_min_priority: policy.data?.sound_min_priority,
          push_min_priority: policy.data?.push_min_priority,
          ...patch,
        }),
      }),
    onSuccess: () => {
      notify({ kind: "success", title: "Notification policy updated" });
      void qc.invalidateQueries({ queryKey: ["alert-policy"] });
    },
    onError: (e) =>
      notify({ kind: "error", title: "Could not update policy", body: e instanceof Error ? e.message : String(e) }),
  });

  // ONE enable for the console (usePushEnable) — the shell's banner uses the same, so the two cannot
  // subscribe differently. It used to ask for permission and stop there: no worker, no subscription.
  const { state: pushState, busy: subscribing, enable } = usePushEnable();

  // This account's own devices (the notify module scopes every route to the caller).
  const devices = useQuery({
    queryKey: ["notify-push-devices"],
    queryFn: () => client.apiFetch<{ subscriptions: AdminPushSubscription[] }>("/notify/push"),
  });

  const testPush = useMutation({
    mutationFn: () => client.apiFetch<{ attempted: number; sent: number; pruned: number }>("/notify/push/test", { method: "POST" }),
    onSuccess: (r) =>
      notify({
        kind: "success",
        title: `Test sent to ${r.sent} of ${r.attempted} device(s)`,
        // The push service accepting it is not the notification arriving — say what silence means.
        body: "If nothing appears within a few seconds, that device's subscription is not reaching you: remove it and enable it again.",
      }),
    onError: (e) => notify({ kind: "error", title: "Test not sent", body: e instanceof Error ? e.message : String(e) }),
  });

  const removeDevice = useMutation({
    mutationFn: (id: number) => client.apiFetch(`/notify/push/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      notify({ kind: "success", title: "Device removed" });
      void qc.invalidateQueries({ queryKey: ["notify-push-devices"] });
    },
    onError: (e) => notify({ kind: "error", title: "Could not remove", body: e instanceof Error ? e.message : String(e) }),
  });

  const deviceCols: DataListColumn<AdminPushSubscription>[] = [
    { key: "user_agent", label: "Device", render: (d) => <span className="truncate">{d.user_agent || "unknown"}</span> },
    { key: "created_at", label: "Added", sortable: true },
    { key: "last_push_at", label: "Last push", render: (d) => d.last_push_at || "never" },
  ];

  return (
    <div>
      <PageHeader
        title="Notifications"
        subtitle="What reaches you, and how loudly. Applies to your own account on this node."
      />

      <StateView loading={policy.isLoading} error={policy.error} errorContext="the notification policy">
        {policy.data && (
          <div className="space-y-4">
            <LevelPicker
              title="Sound"
              help="Plays an audible alert while the console is open. An interruption — set it higher than the push threshold unless you want to hear routine events."
              value={policy.data.sound_min_priority}
              disabled={save.isPending}
              onChange={(v) => save.mutate({ sound_min_priority: v })}
            />
            <LevelPicker
              title="Push notification"
              help="Delivered by the browser even when the console is closed. Requires granting permission below."
              value={policy.data.push_min_priority}
              disabled={save.isPending}
              onChange={(v) => save.mutate({ push_min_priority: v })}
            />

            {policy.data.source && policy.data.source !== "stored" && (
              <Panel className="p-3 text-[12.5px] leading-relaxed text-muted">
                <StatusPill tone="warn">{policy.data.source}</StatusPill>{" "}
                {policy.data.note}
              </Panel>
            )}

            <Section
              title="Push to this browser"
              actions={
                <StatusPill tone={pushState === "granted" ? "ok" : pushState === "denied" ? "bad" : "warn"}>
                  {pushState === "unsupported" ? "unsupported" : pushState === "prompt" ? "not set up" : pushState}
                </StatusPill>
              }
            >
              <p className="text-[13px] leading-relaxed text-muted">
                {pushState === "unsupported"
                  ? "This browser cannot receive push notifications from a website."
                  : pushState === "denied"
                    ? "This browser is blocking notifications. Re-enable them in the site settings — the panel cannot ask again once denied."
                    : "Alerts at or above the push threshold are delivered to every device listed below, even when the console is closed. Each browser is enabled separately."}
              </p>
              {(pushState === "prompt" || pushState === "granted") && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="soft" disabled={subscribing} onClick={() => void enable()}>
                    <Bell size={13} />
                    {subscribing ? "Subscribing…" : "Enable in this browser"}
                  </Button>
                  <Button size="sm" disabled={testPush.isPending} onClick={() => testPush.mutate()}>
                    {testPush.isPending ? "Sending…" : "Send a test"}
                  </Button>
                </div>
              )}
              <div className="mt-3">
                <DataList
                  items={devices.data?.subscriptions ?? []}
                  columns={deviceCols}
                  getRowKey={(d) => String(d.id)}
                  loading={devices.isLoading}
                  error={devices.error}
                  errorContext="your push devices"
                  emptyMessage="No device receives your alerts."
                  emptyHint="Enable this browser above — until a device is subscribed, a push reaches nobody."
                  rowActions={(d) => (
                    <Button size="sm" variant="danger" disabled={removeDevice.isPending} onClick={() => removeDevice.mutate(d.id)}>
                      <BellOff size={13} />
                      Remove
                    </Button>
                  )}
                />
              </div>
            </Section>

            <AlertChannels
              key={(policy.data.webhook_urls ?? []).join(",") + "|" + (policy.data.alert_emails ?? []).join(",")}
              hooks={policy.data.webhook_urls ?? []}
              emails={policy.data.alert_emails ?? []}
              pending={save.isPending}
              onSave={(webhook_urls, alert_emails) => save.mutate({ webhook_urls, alert_emails })}
            />

            <AlertLevels />

            {policy.data.updated_at && (
              <p className="font-mono text-[11.5px] text-faint">
                policy last changed {new Date(policy.data.updated_at).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </StateView>
    </div>
  );
}

function LevelPicker({
  title,
  help,
  value,
  onChange,
  disabled,
}: {
  title: string;
  help: string;
  value: Priority;
  onChange(v: Priority): void;
  disabled?: boolean;
}) {
  return (
    <Panel className="overflow-hidden">
      <div className="border-b border-line px-4 py-3">
        <Eyebrow accent>{title}</Eyebrow>
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-muted">{help}</p>
      </div>
      <div className="divide-y divide-line">
        {LEVELS.map((l) => {
          const on = value === l.key;
          return (
            <button
              key={l.key}
              type="button"
              disabled={disabled}
              onClick={() => onChange(l.key)}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors disabled:opacity-50",
                on ? "bg-accentsoft" : "hover:bg-surface2",
              )}
            >
              <span
                className={cn(
                  "grid size-4 shrink-0 place-items-center rounded-full border",
                  on ? "border-accent" : "border-line2",
                )}
              >
                {on && <span className="size-2 rounded-full bg-accent" />}
              </span>
              <span className="min-w-0">
                <span className={cn("block text-[13.5px]", on ? "font-semibold text-fg" : "text-muted")}>
                  {l.label}
                </span>
                <span className="block text-[12px] text-faint">{l.detail}</span>
              </span>
              {l.key === "crit" && <BellOff size={13} className="ml-auto shrink-0 text-faint" />}
            </button>
          );
        })}
      </div>
    </Panel>
  );
}

// ── What each module watches, and at what level ───────────────────────────────────────────
/**
 * The row exactly as /ozpanel/alerts sends it. Written down separately from AlertRow because it
 * is a different shape: the wire carries `module_title` and this screen works in `moduleTitle`.
 *
 * The call used to assert `AlertRow` — a type declaring a field the server does not send — and
 * then reach past its own declaration with `(a as unknown as { module_title: string })` to get
 * the real one. A cast that contradicts the type beside it is the type admitting it is wrong.
 */
type LevelWire = AlertWire & { module: string; module_title?: string };
/** The same row after this screen has mapped the wire name into its own. */
type AlertRow = AlertWire & { module: string; moduleTitle: string; disabled?: boolean };
interface Override {
  module: string; metric_key: string;
  threshold?: number; deviation_pct?: number; window_seconds?: number; disabled: boolean; updated_by?: string;
}

/** Which level an alert is judged by: the module's declared trigger, else whichever level is set. */
function triggerOf(a: AlertRow): "threshold" | "deviation" {
  if (a.trigger === "threshold" || a.trigger === "deviation") return a.trigger;
  return a.threshold ? "threshold" : "deviation";
}

/** AlertLevels lists every module-declared alert across the platform and lets the operator
 *  change the level.
 *
 *  A module declares a sensible default; this is where THIS node disagrees with it. Until now
 *  those numbers were compile-time constants — alerting at 80% instead of 90% meant editing Go
 *  and redeploying, which is not a configuration interface. */
function AlertLevels() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [editing, setEditing] = React.useState<AlertRow | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const overrides = useQuery({
    queryKey: ["alert-thresholds"],
    queryFn: () => client.apiFetch<{ overrides: Override[] }>("/notify/thresholds"),
    retry: false,
  });

  // ONE call for the whole node. Asking per module meant 47 sequential requests, each
  // running that module's Health() — minutes to paint a settings page.
  const details = useQuery({
    queryKey: ["all-alerts"],
    queryFn: async () => {
      // The wire field is `module_title`; `moduleTitle` is this screen's own name for it. The
      // response type said `AlertRow`, which declares moduleTitle, so it asserted a field the
      // server does not send and then reached past its own type with a cast to get the real one.
      // Typed as the wire shape, mapped once, with no cast.
      const d = await client.apiFetch<{ alerts: LevelWire[] | null }>("/ozpanel/alerts");
      return (d.alerts ?? []).map((a) => ({ ...a, moduleTitle: a.module_title ?? "" }));
    },
  });

  const ovByKey = React.useMemo(() => {
    const m = new Map<string, Override>();
    for (const o of overrides.data?.overrides ?? []) m.set(`${o.module}.${o.metric_key}`, o);
    return m;
  }, [overrides.data]);

  const rows = (details.data ?? []).map((a) => {
    const o = ovByKey.get(`${a.module}.${a.key}`);
    return { ...a, overridden: !!o, disabled: o?.disabled,
      threshold: o?.threshold ?? a.threshold, deviation_pct: o?.deviation_pct ?? a.deviation_pct,
      window_seconds: o?.window_seconds ?? a.window_seconds };
  });

  const save = useMut({
    mutationFn: (v: Record<string, unknown>) =>
      client.apiFetch("/notify/thresholds", { method: "PUT", body: JSON.stringify(v) }),
    onSuccess: () => {
      notify({ kind: "success", title: "Alert level saved", body: "Every node applies it on its next module-health pass, within a minute." });
      void qc.invalidateQueries({ queryKey: ["alert-thresholds"] });
      setEditing(null); setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });
  const revert = useMut({
    mutationFn: (a: AlertRow) =>
      client.apiFetch(`/notify/thresholds?module=${encodeURIComponent(a.module)}&metric_key=${encodeURIComponent(a.key)}`, { method: "DELETE" }),
    onSuccess: () => {
      notify({ kind: "success", title: "Reverted to the module's own level" });
      void qc.invalidateQueries({ queryKey: ["alert-thresholds"] });
    },
  });

  const level = (a: AlertRow) =>
    a.disabled ? "silenced"
    : a.threshold ? `≥ ${a.threshold}${a.unit ? " " + a.unit : ""}${a.window_seconds ? ` over ${windowLabel(a.window_seconds)}` : ""}`
    : a.deviation_pct ? `${a.deviation_pct}% over average` : "—";

  const columns: DataListColumn<AlertRow>[] = [
    { key: "moduleTitle", label: "Module", sortable: true },
    { key: "label", label: "Watches", sortable: true },
    {
      key: "level", label: "Alerts at", value: level,
      render: (a) => (
        <span className="flex items-center gap-2">
          <span className="font-mono tabular">{level(a)}</span>
          {a.disabled && <StatusPill tone="warn">off</StatusPill>}
          {a.overridden && !a.disabled && <StatusPill tone="accent">custom</StatusPill>}
        </span>
      ),
    },
    {
      // What it reads NOW on this node, and why there is no reading when there is none — an alert that
      // cannot be measured cannot fire, and a level table that hid that read as fully armed.
      key: "now", label: "Now", value: (a) => a.reason ?? (a.breached ? "breached" : "live"),
      render: (a) => <AlertReading a={a} />,
    },
    { key: "interval_seconds", label: "Checked", value: (a) => a.interval_seconds ?? 0, render: (a) => `every ${a.interval_seconds}s` },
  ];

  return (
    <Section title="Alert levels" collapsible id="notif.levels"
      hint={`${rows.length} across ${new Set(rows.map((r) => r.module)).size} modules`} bodyClassName="p-0">
      <div className="p-4">
        <p className="mb-3 text-[12.5px] leading-relaxed text-muted">
          Each module declares what it watches and a default level. Change one here when that
          default is wrong for this node — a value with no override uses what the module
          declares, which is never the same as no alert at all.
        </p>
        <DataList
          items={rows}
          columns={columns}
          getRowKey={(a) => `${a.module}.${a.key}${a.instance ? `[${a.instance}]` : ""}`}
          searchable={rows.length > 8}
          loading={details.isLoading}
          error={details.error}
          errorContext="alert levels"
          emptyMessage="No module declares an alert yet."
          emptyHint="Modules opt in by implementing module.Alerting; health and service-lifecycle events are watched for every module regardless."
          rowActions={(a) => (
            <div className="flex gap-1">
              <button type="button" aria-label={`Edit ${a.label}`}
                onClick={(e) => { e.stopPropagation(); setError(null); setEditing(a); }}
                className="rounded p-1 text-faint hover:bg-surface2 hover:text-accent">✎</button>
              {a.overridden && (
                <button type="button" aria-label={`Revert ${a.label}`} disabled={revert.isPending}
                  onClick={(e) => { e.stopPropagation(); revert.mutate(a); }}
                  className="rounded p-1 text-faint hover:bg-surface2 hover:text-accent">↺</button>
              )}
            </div>
          )}
        />
        <div className="mt-3 space-y-1.5">
          {rows.filter((a) => a === rows.find((b) => b.module === a.module && b.key === a.key)).map((a) => (
            <p key={`${a.module}.${a.key}`} className="text-[12px] leading-relaxed text-muted">
              <span className="font-mono text-faint">{a.module}.{a.key}</span> — {a.because}
            </p>
          ))}
        </div>
      </div>
      {editing && (
        <FormDialog
          open
          onOpenChange={(o) => { if (!o) { setEditing(null); setError(null); } }}
          title={`${editing.moduleTitle} — ${editing.label}`}
          description={editing.because}
          submitLabel="Save level"
          pending={save.isPending}
          error={error}
          fields={[
            // The field the metric is JUDGED by. The server sends both levels (0 for the unused one), so
            // the old "threshold present and deviation absent" test was never true and every edit wrote
            // deviation_pct — on a threshold metric, a value nothing evaluates. The declared trigger
            // decides; a server without it falls back to whichever level is set.
            ...(triggerOf(editing) === "threshold"
              ? [{ key: "threshold", label: `Alert at or above${editing.unit ? " (" + editing.unit + ")" : ""}`, type: "int" as const, defaultValue: editing.threshold || undefined }]
              : [{ key: "deviation_pct", label: "Alert this % above its own average", type: "int" as const, defaultValue: editing.deviation_pct || undefined }]),
            // An alert read over a choice of averaging windows (CPU pressure: the kernel's 10 s, 1 min, 5 min).
            ...(editing.windows_seconds && editing.windows_seconds.length > 0
              ? [{ key: "window_seconds", label: "Averaged over", type: "enum" as const,
                  defaultValue: String(editing.window_seconds ?? editing.windows_seconds[0]),
                  options: editing.windows_seconds.map((s) => ({
                    value: String(s),
                    label: windowLabel(s) + (s === (editing.declared_window_seconds ?? (editing.overridden ? undefined : editing.window_seconds)) ? " (recommended)" : ""),
                  })),
                  help: "A shorter window answers sooner and follows brief bursts; a longer one only sustained load." }]
              : []),
            { key: "disabled", label: "Silence this check", type: "bool", defaultValue: !!editing.disabled,
              help: "Kept and still collected — the value stays visible on the module page, no event is raised." },
          ]}
          onSubmit={(v) => {
            // Only the field this metric is judged by is sent: the other stays un-overridden.
            const field = triggerOf(editing) === "threshold" ? "threshold" : "deviation_pct";
            const raw = v[field];
            save.mutate({
              module: editing.module, metric_key: editing.key,
              [field]: raw !== undefined && raw !== "" ? Number(raw) : undefined,
              window_seconds: editing.windows_seconds?.length && v.window_seconds ? Number(v.window_seconds) : undefined,
              disabled: !!v.disabled,
            });
          }}
        />
      )}
    </Section>
  );
}

/** windowLabel names an averaging window: 10 → "10 s", 60 → "1 min", 300 → "5 min". */
const windowLabel = (s: number) => (s < 60 || s % 60 !== 0 ? `${s} s` : `${s / 60} min`);

const splitList = (s: string) =>
  s
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter(Boolean);

// AlertChannels — where alerts go besides the browsers below. Both work while the cluster's database is
// down (2026-10-24: a 9-minute outage reached nobody): webhooks get one JSON object per alert, and e-mail is
// delivered straight to the recipient's mail server, not through this cluster's own mail system.
function AlertChannels({
  hooks,
  emails,
  pending,
  onSave,
}: {
  hooks: string[];
  emails: string[];
  pending: boolean;
  onSave: (hooks: string[], emails: string[]) => void;
}) {
  const [h, setH] = React.useState(hooks.join("\n"));
  const [e, setE] = React.useState(emails.join("\n"));
  const input = "w-full rounded border border-line2 bg-bg px-3 py-2 font-mono text-[12.5px] outline-none focus:border-accent";
  return (
    <Section
      title="Also send alerts to"
      actions={
        <Button size="sm" variant="soft" disabled={pending} onClick={() => onSave(splitList(h), splitList(e))}>
          {pending ? "Saving…" : "Save"}
        </Button>
      }
    >
      <p className="text-[13px] leading-relaxed text-muted">
        Every alert at or above the push threshold also goes here. Both keep working while the cluster&apos;s database is
        down, which is when push devices are all you would otherwise have.
      </p>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        <label className="block text-[12.5px]">
          Webhooks (https, one per line) — each receives a JSON object per alert
          <textarea className={input} rows={3} value={h} onChange={(ev) => setH(ev.target.value)} placeholder="https://hooks.example/ozpanel" />
        </label>
        <label className="block text-[12.5px]">
          E-mail addresses (one per line) — delivered directly to the recipient&apos;s mail server
          <textarea className={input} rows={3} value={e} onChange={(ev) => setE(ev.target.value)} placeholder="owner@example.com" />
        </label>
      </div>
    </Section>
  );
}
